import React from 'react'
import { useParams } from 'react-router-dom'
import { useLearningItem } from '../../state/entities'
import { useTranslation } from 'react-i18next'
import { User } from '../../models/User'
import UserAvatar from '../../components/UserAvatar/UserAvatar'
import styles from './EventDateAttendeesList.module.scss'
import PersonOffIcon from '@material-symbols/svg-300/outlined/person_off.svg'
import BeenHereIcon from '@material-symbols/svg-300/outlined/beenhere.svg'
import NotListedLocationIcon from '@material-symbols/svg-300/outlined/not_listed_location.svg'
import { Formik, FormikValues } from 'formik'
import FormButtons from '../../components/basics/FormButtons/FormButtons'
import { Attendee } from '../../models/Attendee'
import { CheckboxFormik } from '../../components/basics/Checkbox/Checkbox'
import { useSession } from '../../state/session'
import useUpdateAttendees, { AttendeeUpdateEntry } from '../../state/mutations/useUpdateAttendees'
import { getEventAndDateFromLearningItem } from '../../state/calculations/learningItemHelpers'
import { getAttendeeByUserUuid } from '../../state/calculations/attendeesHelpers'
import { userCanUpdateAttendance } from '../../state/calculations/permissions/userCan'

interface EventDateAttendeesListProps {
  onClose: () => void
}

export default React.memo(function EventDateAttendeesList({ onClose }: EventDateAttendeesListProps) {
  const { uuid, eventUuid, dateUuid } = useParams()
  if (!uuid || !eventUuid || !dateUuid) {
    throw new Error('Missing required URL parameters: uuid, eventUuid, dateUuid')
  }

  const { user: currentUser } = useSession()
  if (!currentUser) {
    throw new Error('User must be logged in')
  }

  const { t } = useTranslation()

  const { learningItem } = useLearningItem(uuid)

  const updateAttendees = useUpdateAttendees()

  if (learningItem.isDummy) {
    return null
  }

  const { event, date } = getEventAndDateFromLearningItem(learningItem, eventUuid, dateUuid)

  const formInitialValues = getFormInitialValues(event.participants, date.attendees)

  const allowedToUpdate = userCanUpdateAttendance(currentUser, date.trainers)

  return (
    <article key={dateUuid}>
      <header>
        <h3>{t('attendees.attendance tracking')}</h3>
        <p>{learningItem.title}</p>
        <p>{t('format.DATETIMERANGE', { range: { start: date.startDate, end: date.endDate } })}</p>
      </header>

      <Formik
        initialValues={formInitialValues}
        onSubmit={async (values, { setStatus }) => {
          const valuesForSubmit = prepareFormValuesForSubmit(values, date.attendees, date.uuid)

          try {
            await updateAttendees.mutateAsync({
              dateUuid: date.uuid,
              entries: valuesForSubmit,
            })

            onClose()
          } catch (err) {
            setStatus(err)
          }
        }}
      >
        {(props) => (
          <form noValidate onSubmit={props.handleSubmit}>
            {event.participants.length > 0 && (
              <table className={styles.attendeesTable}>
                <thead>
                  <tr>
                    <th className={styles.avatarColumn}></th>
                    <th className={styles.nameColumn}>{t('name')}</th>
                    <th className={styles.actionColumn}></th>
                  </tr>
                </thead>

                <tbody>
                  {event.participants.map((participant: User) => {
                    return (
                      <tr key={participant.uuid}>
                        <td className={styles.avatarColumn}>
                          <UserAvatar user={participant} />
                        </td>

                        <td className={styles.nameColumn}>{`${participant.firstname} ${participant.lastname}`}</td>

                        <td className={styles.actionColumn}>
                          <div className={styles.selectAttendanceActions}>
                            <div>
                              <CheckboxFormik
                                id={`action-${participant.uuid}-attended`}
                                plain
                                type="radio"
                                required
                                disabled={!allowedToUpdate}
                                name={`action-${participant.uuid}`}
                                value="attended"
                              />
                              <label htmlFor={`action-${participant.uuid}-attended`}>
                                <BeenHereIcon />
                              </label>
                            </div>

                            <div>
                              <CheckboxFormik
                                id={`action-${participant.uuid}-unknown`}
                                plain
                                type="radio"
                                required
                                disabled={!allowedToUpdate}
                                name={`action-${participant.uuid}`}
                                value="unknown"
                              />
                              <label htmlFor={`action-${participant.uuid}-unknown`}>
                                <NotListedLocationIcon />
                              </label>
                            </div>

                            <div>
                              <CheckboxFormik
                                id={`action-${participant.uuid}-not-attended`}
                                plain
                                type="radio"
                                required
                                disabled={!allowedToUpdate}
                                name={`action-${participant.uuid}`}
                                value="not-attended"
                              />
                              <label htmlFor={`action-${participant.uuid}-not-attended`}>
                                <PersonOffIcon />
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}

            {event.participants.length === 0 && (
              <div className={styles.noAttendees}>
                <h3>{t('attendees.no attendees')}</h3>
              </div>
            )}

            <FormButtons
              onClose={onClose}
              showSubmit={event.participants.length > 0 && allowedToUpdate}
              submitLabel={t('attendees.cta tracking completed')}
              extraClassName={styles.bottomButtons}
            />
          </form>
        )}
      </Formik>
    </article>
  )
})

function prepareFormValuesForSubmit(
  values: FormikValues,
  attendees: Attendee[],
  dateUuid: string
): AttendeeUpdateEntry[] {
  return Object.entries(values).map(([key, status]) => {
    const [_, userUuid] = key.split('action-')

    const attendee = getAttendeeByUserUuid(userUuid, attendees)

    // [Attendee = null] happens when the user's attendance
    // status was not yet stored in the attendees' table.

    return {
      userUuid,
      status,
      dateUuid,
      bookingUuid: attendee ? attendee.booking.uuid : null,
    }
  })
}

function getFormInitialValues(
  participants: { uuid: string }[],
  attendees: Attendee[]
): {
  [key: string]: 'attended' | 'not-attended' | 'unknown'
} {
  const initialValues: FormikValues = {}

  participants.forEach((participant) => {
    const attendee = getAttendeeByUserUuid(participant.uuid, attendees)

    initialValues[`action-${participant.uuid}`] =
      !attendee || attendee.attended === null ? 'unknown' : attendee.attended ? 'attended' : 'not-attended'
  })

  return initialValues
}
